import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import {
  Box, Chip, Divider, Typography, Avatar,
} from '@mui/material'
import {
  Phone as PhoneIcon,
  Person as PersonIcon,
  CallMade as OutgoingIcon,
  CallReceived as IncomingIcon,
  AccessTime as ClockIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material'
import { CallLog } from '../../lib/api/callLogsApi'
import { salesApi } from '../../lib/api/salesApi'
import { themeColors } from '../../app/layout/theme'
import { EntityModal } from './EntityModal'
import { AudioPlayer } from './AudioPlayer'
import { maskPhoneNumber } from '../../utils/phoneUtils'

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function formatDuration(sec: number | null | undefined) {
  if (sec == null || sec <= 0) return '—'
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  CONNECTED:    { bg: '#dcfce7', text: '#15803d' },
  INITIATED:    { bg: '#e0f2fe', text: '#0369a1' },
  BUSY:         { bg: '#ffedd5', text: '#c2410c' },
  NOT_ANSWERED: { bg: '#fee2e2', text: '#dc2626' },
  FAILED:       { bg: '#fee2e2', text: '#dc2626' },
}

function getStatusStyle(status: string) {
  const normalized = (status || '').toUpperCase().replace('-', '_')
  return STATUS_COLORS[normalized] ?? { bg: '#f3f4f6', text: '#4b5563' }
}

function getCallStatusDetailText(call: CallLog) {
  if (call.agentCallStatus || call.customerCallStatus) {
    const parts: string[] = []
    if (call.agentCallStatus) parts.push(`Agent: ${call.agentCallStatus}`)
    if (call.customerCallStatus) parts.push(`Customer: ${call.customerCallStatus}`)
    return parts.join(' | ')
  }
  return null
}

interface Props {
  open: boolean
  onClose: () => void
  call: CallLog | null
}

export function CallDetailModal({ open, onClose, call }: Props) {
  const navigate = useNavigate()
  const [associatedSale, setAssociatedSale] = useState<{ id: string; dealName: string } | null>(null)

  useEffect(() => {
    if (call?.dealId) {
      salesApi.getByDealId(call.dealId)
        .then(sale => {
          if (sale && sale.id) {
            setAssociatedSale({ id: sale.id, dealName: sale.dealName })
          } else {
            setAssociatedSale(null)
          }
        })
        .catch(() => {
          setAssociatedSale(null)
        })
    } else {
      setAssociatedSale(null)
    }
  }, [call?.dealId])

  if (!call) return null

  const sc = getStatusStyle(call.status)
  const detailText = getCallStatusDetailText(call)
  const isOutgoing = call.callType === 'OUTGOING'
  const personName = call.leadName || call.contactName || call.dealContactName || call.retentionCustomerName || 'Unknown'

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Call Details"
      icon={<PhoneIcon />}
      maxWidth="md"
      actions={
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary }}>
            Logged at {formatDate(call.createdAt)}
          </Typography>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        
        {/* Info Grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '130px 1fr', rowGap: 1.5, alignItems: 'center' }}>
          
          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>Participant</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 24, height: 24, fontSize: '11px', bgcolor: themeColors.primary }}>
              {personName.charAt(0).toUpperCase()}
            </Avatar>
            <Typography sx={{ fontSize: '14px', color: themeColors.text.primary, fontWeight: 700 }}>
              {personName}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>Phone Number</Typography>
          <Typography sx={{ fontSize: '13px', color: themeColors.text.primary, fontFamily: 'monospace' }}>
            {maskPhoneNumber(call.leadPhone || call.contactPhone || call.dealContactPhone)}
          </Typography>

          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>Call Direction</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            {isOutgoing ? (
              <OutgoingIcon sx={{ fontSize: 16, color: '#0284c7' }} />
            ) : (
              <IncomingIcon sx={{ fontSize: 16, color: '#16a34a' }} />
            )}
            <Typography sx={{ fontSize: '13px', color: themeColors.text.primary, fontWeight: 600 }}>
              {isOutgoing ? 'Outgoing' : 'Incoming'}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>Initiated By</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PersonIcon sx={{ fontSize: 16, color: themeColors.text.secondary }} />
            <Typography sx={{ fontSize: '13px', color: themeColors.text.primary }}>
              {call.initiatedBy?.name ?? 'System'}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>Started At</Typography>
          <Typography sx={{ fontSize: '13px', color: themeColors.text.primary }}>
            {formatDate(call.startedAt)}
          </Typography>

          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>Duration</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ClockIcon sx={{ fontSize: 15, color: themeColors.text.secondary }} />
            <Typography sx={{ fontSize: '13px', color: themeColors.text.primary }}>
              {formatDuration(call.durationSeconds)}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>Status</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label={call.status}
              size="small"
              sx={{ fontSize: '11px', height: 22, bgcolor: sc.bg, color: sc.text, fontWeight: 700, border: 'none' }}
            />
            {detailText && (
              <Typography sx={{ fontSize: '12px', color: themeColors.text.secondary, fontStyle: 'italic' }}>
                ({detailText})
              </Typography>
            )}
          </Box>

          {call.outcome && (
            <>
              <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600 }}>Outcome</Typography>
              <Box>
                <Chip
                  label={call.outcome.replace('_', ' ')}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '11px', height: 22, fontWeight: 600, borderColor: themeColors.primary, color: themeColors.primary }}
                />
              </Box>
            </>
          )}

          {/* Conditional Deep Links */}
          {(call.leadId || call.contactId || call.dealId || call.retentionCustomerId || call.salesId || associatedSale) && (
            <>
              <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, fontWeight: 600, mt: 1 }}>Related Records</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
                {call.leadId && (
                  <Chip
                    icon={<LaunchIcon sx={{ fontSize: '12px !important' }} />}
                    label="Lead Details"
                    onClick={() => { onClose(); navigate(`/leads/${call.leadId}`) }}
                    sx={{ fontSize: '11px', height: 24, cursor: 'pointer' }}
                  />
                )}
                {call.contactId && (
                  <Chip
                    icon={<LaunchIcon sx={{ fontSize: '12px !important' }} />}
                    label="Contact Details"
                    onClick={() => { onClose(); navigate(`/contacts/${call.contactId}`) }}
                    sx={{ fontSize: '11px', height: 24, cursor: 'pointer' }}
                  />
                )}
                {call.dealId && (
                  <Chip
                    icon={<LaunchIcon sx={{ fontSize: '12px !important' }} />}
                    label="Deal Details"
                    onClick={() => { onClose(); navigate(`/deals/${call.dealId}`) }}
                    sx={{ fontSize: '11px', height: 24, cursor: 'pointer' }}
                  />
                )}
                {call.salesId && (
                  <Chip
                    icon={<LaunchIcon sx={{ fontSize: '12px !important' }} />}
                    label="Sale Details"
                    onClick={() => { 
                      onClose(); 
                      navigate(`/sales/${call.salesId || associatedSale?.id}`) 
                    }}
                    sx={{ fontSize: '11px', height: 24, cursor: 'pointer', bgcolor: '#fef9c3', color: '#854d0e' }}
                  />
                )}
                {call.retentionCustomerId && (
                  <Chip
                    icon={<LaunchIcon sx={{ fontSize: '12px !important' }} />}
                    label="Retention Details"
                    onClick={() => { onClose(); navigate(`/customer-retention/${call.retentionCustomerId}`) }}
                    sx={{ fontSize: '11px', height: 24, cursor: 'pointer', bgcolor: '#f5f3ff', color: '#6d28d9' }}
                  />
                )}
              </Box>
            </>
          )}

        </Box>

        <Divider />

        {/* Audio Recording Section */}
        <Box>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: themeColors.text.secondary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Call Recording
          </Typography>
          {call.recordingUrl ? (
            <Box sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '8px', p: 2, bgcolor: '#fbfbfb' }}>
              <AudioPlayer recordingUrl={call.recordingUrl} durationSeconds={call.durationSeconds ?? undefined} />
            </Box>
          ) : (
            <Box sx={{ border: `1px solid ${themeColors.border}`, borderRadius: '8px', p: 2, bgcolor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>
                No recording available for this call
              </Typography>
            </Box>
          )}
        </Box>

        {/* Notes Section */}
        <Box>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: themeColors.text.secondary, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Notes / Details
          </Typography>
          <Box
            sx={{
              border: `1px solid ${themeColors.border}`, borderRadius: '8px',
              p: 2, bgcolor: '#fff', fontSize: '13.5px', color: themeColors.text.primary,
              lineHeight: 1.6, maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap',
            }}
          >
            {call.notes ? call.notes : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No notes logged</span>}
          </Box>
        </Box>

      </Box>
    </EntityModal>
  )
}
